import { useTranslation } from "react-i18next";
import {
  SegmentedField,
  type SegmentedFieldOption,
} from "@/components/ui/SegmentedField";
import {
  DEFAULT_PRIORITY,
  normalizePriority,
  PRIORITY_LEVELS,
  type PriorityLevel,
} from "@/lib/priority";

interface Props {
  /**
   * Unique id — the segmented group wires its `aria-labelledby` to it, so two
   * priority fields on one screen (create + bulk edit) keep distinct accessible
   * names (the BUG-504/508 family).
   */
  id: string;
  value: PriorityLevel | number | string | null | undefined;
  onChange: (next: PriorityLevel) => void;
  className?: string;
  /** Forwarded to {@link SegmentedField} — see its `labelVariant` doc. */
  labelVariant?: "section" | "field";
}

/**
 * The ONLY priority input in the app (TASK-4833).
 *
 * Owner ruling, 2026-08-23: «давай вообще уберем числовые приоритеты, и сделаем
 * низкий средний высокий». There is no numeric priority input anywhere — this
 * component replaced the `<input type="number" min=0 max=100>` in the create
 * order form, the order edit form, both permanent-order forms and the bulk-edit
 * menu. A number field here would be a real defect and not just a style issue:
 * the backend column is a three-value enum, so 75 is simply not a value it can
 * store.
 *
 * `value` is deliberately tolerant of a number — a form initialised from a stale
 * cached row can still carry one — and `normalizePriority` maps it onto a level
 * using the same thresholds the database migration used, so what the control
 * shows always matches what was actually stored.
 */
export function PriorityField({
  id,
  value,
  onChange,
  className,
  labelVariant,
}: Props) {
  const { t } = useTranslation();

  const options: SegmentedFieldOption<PriorityLevel>[] = PRIORITY_LEVELS.map(
    (level) => ({
      value: level,
      label: t(`orders.priorityLevel.${level}`),
    }),
  );

  return (
    <SegmentedField<PriorityLevel>
      id={id}
      label={t("orders.form.priority")}
      value={value == null ? DEFAULT_PRIORITY : normalizePriority(value)}
      onChange={onChange}
      options={options}
      className={className}
      labelVariant={labelVariant}
    />
  );
}
